import { useEffect, useState } from "react";
import {
  InventoryItem,
  YieldCalculationResults,
} from "../../external/inventory-component/types";
import { createWorkerFactory, useWorker } from "@shopify/react-web-worker";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Spinner from "react-bootstrap/Spinner";

export interface YieldCalculationProps {
  inventoryId: string;
  inventoryItem?: InventoryItem;
  authenticatedUserToken: string;
}

const createInventoryWorker = createWorkerFactory(
  () => import("../../workers/inventory")
);

const YieldCalculation = ({
  inventoryId,
  authenticatedUserToken,
}: YieldCalculationProps) => {
  const inventoryWorker = useWorker(createInventoryWorker);
  const [calculationResults, setCalculationResults] =
    useState<YieldCalculationResults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const fetchData = async (id: string) => {
      try {
        const result = await inventoryWorker.getYieldCalculation(
          authenticatedUserToken,
          id
        );
        if (result.data) {
          setCalculationResults(result.data);
          setError(null);
        } else if (result.error) {
          setError(result.error.message);
          setCalculationResults(null);
        }
      } catch (err) {
        setError("An unexpected error occurred");
        setCalculationResults(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData(inventoryId);
  }, [inventoryId, authenticatedUserToken, inventoryWorker]);

  const getExitBadgeVariant = (exit: string | undefined) => {
    switch (exit) {
      case "Auction":
        return "warning";
      case "Retail":
        return "success";
      default:
        return "secondary";
    }
  };

  if (loading) {
    return (
      <Card className="mb-3 shadow-sm">
        <Card.Body className="d-flex justify-content-center py-3">
          <Spinner animation="border" variant="primary" size="sm" />
          <span className="ms-2">Loading calculation...</span>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="mb-3 shadow-sm" border="danger">
        <Card.Body className="p-2">
          <Card.Title className="fs-6 text-danger mb-0">
            Yield Calculation Incomplete
          </Card.Title>
          <small className="text-muted">{error}</small>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="mb-3 shadow-sm">
      <Card.Header className="py-2 d-flex justify-content-between align-items-center">
        <span className="fw-bold">Yield Calculation</span>
        {calculationResults?.exit && (
          <Badge bg={getExitBadgeVariant(calculationResults.exit)} pill>
            {calculationResults.exit}
          </Badge>
        )}
      </Card.Header>
      <Card.Body className="p-0">
        <table className="table table-sm m-0">
          <tbody className="small">
            <tr>
              <td className="text-muted ps-3">Final Size</td>
              <td className="fw-semibold text-end pe-3">
                {calculationResults?.finalSize || "—"}
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-3">PF Girth</td>
              <td className="fw-semibold text-end pe-3">
                {calculationResults?.pfGirth || "—"}
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-3">CBM</td>
              <td className="fw-semibold text-end pe-3">
                {calculationResults?.cbm || "—"}
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-3">One Man</td>
              <td className="fw-semibold text-end pe-3">
                {calculationResults?.oneManService || "—"}
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-3">Two Man</td>
              <td className="fw-semibold text-end pe-3">
                {calculationResults?.twoManService || "—"}
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-3">Weight</td>
              <td className="fw-semibold text-end pe-3">
                {calculationResults?.itemDetail?.weight
                  ? `${calculationResults.itemDetail.weight} kg`
                  : "—"}
              </td>
            </tr>
            <tr>
              <td className="text-muted ps-3">RRP</td>
              <td className="fw-semibold text-end pe-3">
                {!calculationResults?.itemDetail?.rrp
                  ? "n/a"
                  : `£${(calculationResults.itemDetail.rrp / 100).toFixed(2)}`}
              </td>
            </tr>
          </tbody>
        </table>
      </Card.Body>
    </Card>
  );
};

export default YieldCalculation;
